import { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Caption1, TabValue } from '@fluentui/react-components';
import { AddRegular, CheckmarkFilled, EditRegular } from '@fluentui/react-icons';

import { TOverflowButtonParam } from '@shared/components';
import { useDocumentTitle } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import { useRegisterAiAssistantOrigin } from '@modules/ai/hooks/useRegisterAiAssistantOrigin.hook';
import { EAiAssistantOriginItemType } from '@modules/ai/types';

import { ItemsNavDrawer, TFolder, TPath, TSparseItem } from '@modules/items';
import { TPermissions, useCanEdit } from '@modules/access';

import { TLiterature, TLiteratureAssessment, TLiteratureResolvedItems } from '../types';
import { useCreateLiteratureDraft } from '../api';
import { useLiteratureView } from '../hooks/useLiteratureView.hook';
import LiteratureHeader from './LiteratureHeader';
import LiteratureContent from './LiteratureContent';
import LiteratureNavOverview from './LiteratureNavOverview';
import LiteratureDialog from './dialogs/LiteratureDialog';
import CompleteLiteratureDialog from './dialogs/CompleteLiteratureDialog';
import AssessmentDialog, { TAssessmentDialogState } from './dialogs/AssessmentDialog';
import AssessmentsTab from './tabs/AssessmentsTab';
import HistoryTab from './tabs/HistoryTab';

type TProps = {
  folder: TFolder;
  literature: TLiterature & { resolvedPath: TSparseItem[] };
  permissions: TPermissions;
  resolvedItems: TLiteratureResolvedItems;
};

const LiteratureNavInsights = () => (
  <div className="px-5 py-4">
    <Caption1 className="text-neutral-500">No insights yet.</Caption1>
  </div>
);

const LiteratureContainer = ({ folder, literature, permissions, resolvedItems }: TProps) => {
  useRegisterAiAssistantOrigin({ item: literature._id, itemType: EAiAssistantOriginItemType.LITERATURE_ENTRY });

  const folderId = folder._id;

  const [selectedTab, setSelectedTab] = useState<TabValue>('home');
  const [isOpenDrawer, setIsOpenDrawer] = useState(true);
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 1550);
  const [isLiteratureDialogOpen, setIsLiteratureDialogOpen] = useState(false);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [assessmentDialogState, setAssessmentDialogState] = useState<TAssessmentDialogState>({ isOpen: false });

  const { version } = useLiteratureView(literature, permissions.accessRight);
  const content = version?.content;
  useDocumentTitle(content?.title ?? TERMINOLOGY.literature.upper);

  const createDraft = useCreateLiteratureDraft();

  const { canEdit } = useCanEdit(permissions.accessRight);

  const handleEditDraft = () => {
    if (createDraft.isPending) return;
    setSelectedTab('home');
    if (!literature.draft) {
      createDraft.mutate(literature._id, { onSuccess: () => setIsLiteratureDialogOpen(true) });
    } else {
      setIsLiteratureDialogOpen(true);
    }
  };

  const handleComplete = () => setIsCompleteDialogOpen(true);

  const handleAddAssessment = () => {
    setAssessmentDialogState({ isOpen: true });
    setSelectedTab('assessments');
  };

  const handleOpenAssessment = (assessment: TLiteratureAssessment) => {
    setAssessmentDialogState({ isOpen: true, assessment });
  };

  const overviewActions: TOverflowButtonParam[] = [];
  if (canEdit) {
    overviewActions.push({
      appearance: 'primary' as const,
      children: literature.draft ? 'Edit draft' : 'Create draft',
      icon: <EditRegular />,
      onClick: handleEditDraft,
      disabled: createDraft.isPending,
    });
    if (literature.draft) {
      overviewActions.push({
        children: 'Complete version',
        icon: <CheckmarkFilled />,
        onClick: handleComplete,
      });
    }
    overviewActions.push({
      children: 'Add assessment',
      icon: <AddRegular />,
      onClick: handleAddAssessment,
    });
  }

  const tabs: Record<string, ReactNode> = {
    Overview: <LiteratureNavOverview literature={literature} version={version} actions={overviewActions} />,
    Insights: <LiteratureNavInsights />,
  };

  const resolvedPath: TPath = [
    { _id: folderId, name: folder.name, accessRight: folder.accessRight },
    { _id: literature._id, name: content?.title ?? '', accessRight: permissions.accessRight },
  ];

  useEffect(() => {
    const handleResize = () => setIsWideScreen(window.innerWidth > 1550);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  let maxWidth = 794 + 2 * 40;
  if (isWideScreen || !isOpenDrawer) maxWidth = Math.max(maxWidth, 1024);
  else maxWidth = Math.max(maxWidth, 1152);
  const paddingRight = isOpenDrawer && isWideScreen ? 315 : 0;

  return (
    <div className="flex flex-1 min-h-0" style={{ paddingRight }}>
      <ItemsNavDrawer
        isOpen={isOpenDrawer}
        setIsOpen={setIsOpenDrawer}
        tabs={tabs}
        resolvedPath={resolvedPath}
        hrefBack="/literatures"
      />

      <LiteratureDialog isOpen={isLiteratureDialogOpen} setIsOpen={setIsLiteratureDialogOpen} literature={literature} />
      <CompleteLiteratureDialog
        isOpen={isCompleteDialogOpen}
        setIsOpen={setIsCompleteDialogOpen}
        literature={literature}
      />
      <AssessmentDialog
        literatureId={literature._id}
        assessments={literature.assessments}
        state={assessmentDialogState}
        setState={setAssessmentDialogState}
      />

      <div
        className={classNames('relative grow h-full min-h-0 px-4', {
          'mx-auto pl-3': isWideScreen || !isOpenDrawer,
        })}
        style={{ maxWidth: `${maxWidth}px` }}
      >
        <div className="h-full min-h-0 flex flex-col">
          <LiteratureHeader
            literature={literature}
            version={version}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            permissions={permissions}
            handleEditDraft={handleEditDraft}
            handleComplete={handleComplete}
            isCreatingDraft={createDraft.isPending}
          />
          <div className="w-full flex-1 min-h-0 relative overflow-y-auto">
            {selectedTab === 'assessments' ? (
              <AssessmentsTab
                literatureId={literature._id}
                assessments={literature.assessments}
                resolvedItems={resolvedItems}
                canEdit={canEdit}
                hasDraft={!!literature.draft}
                onAdd={handleAddAssessment}
                onEdit={handleOpenAssessment}
              />
            ) : selectedTab === 'history' ? (
              <HistoryTab literature={literature} setSelectedTab={setSelectedTab} />
            ) : content ? (
              <LiteratureContent content={content} permissions={permissions} />
            ) : (
              <Caption1 className="py-6 text-neutral-500">No content available.</Caption1>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiteratureContainer;
